'use client'

import { useEffect, useState } from 'react'
import {
  DEFAULT_TAX_REGION,
  resolveTaxRegionFromProvince,
  TaxRegion,
} from '@repo/types/common/tax-region'
import { normalizeSpainProvinceCode } from '@repo/types/common/spain-provinces'
import { useCustomerStore } from '@/hooks/use-customer/store'
import { provinceSelector } from '@/hooks/use-customer/selectors'

export type TaxRegionState = {
  /** Provincia del paso 2 (ISO 3166-2:ES) o '' si aún no se ha elegido. */
  province: string
  /** Región con la que se calcula el desglose. Sin provincia, península. */
  taxRegion: TaxRegion
  /**
   * false mientras no hay provincia: el carrito dice «Impuestos incluidos» en
   * vez de afirmar «IVA (21%)» (decisión D9).
   */
  hasProvince: boolean
}

/**
 * Región fiscal para los componentes que la pintan EN EL RENDER (el carrito).
 *
 * La guarda de hidratación no es decorativa: el store se persiste en
 * localStorage y zustand lo rehidrata antes del primer render de cliente, así
 * que sin ella el HTML del servidor (sin provincia) y el del cliente (con
 * provincia canaria) divergirían y React avisaría de hydration mismatch.
 * Hasta el mount se responde como si no hubiera provincia, que es justo lo
 * que ya se enseña en los pasos 1 y 2.
 */
export const useTaxRegion = (): TaxRegionState => {
  const storedProvince = useCustomerStore(provinceSelector)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => setHydrated(true), [])

  const province = hydrated
    ? (normalizeSpainProvinceCode(storedProvince) ?? '')
    : ''

  return {
    province,
    taxRegion: resolveTaxRegionFromProvince(province) ?? DEFAULT_TAX_REGION,
    hasProvince: !!province,
  }
}

export default useTaxRegion
